import React, { useEffect, useState } from "react";
import TopAppBar from "../../components/TopAppBar";
import Layout from "../../components/Layout";
import MainLayout from "../../layouts/MainLayout";
import { useContentStore } from "../../stores/contentStore";
import { locale } from "../../utils/LocaleManager";
import { useNavigate, useParams } from "react-router-dom";
import {
  deleteContentByID,
  editVideo,
  getVideoByID,
  getVideoFromDB,
} from "../../services/contentServices";
import { Bounce, toast } from "react-toastify";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import DialogBox from "../../components/DialogBox";
import {
  colorsCodes,
  convertTimeString,
  handleError,
  toPercentage,
} from "../../utils/utilFunctions";
import {
  antiFlashWhite,
  black,
  darkLimeGreen,
  strongOrange,
  vividRed,
} from "../../utils/StyleHelpers";
import DataTable from "../../components/DataTable";
import { DeleteTags, EditVideo } from "../../models/editDetails";
import TextFieldInput from "../../components/Textfield";
import {
  StyledMUISelect,
  StyledTextFieldItem,
} from "../../components/Layout/styled";
import { TagTypeEnum } from "../../enums/TagTypeEnum";
import Circle from "../../components/Circle";

const DetailsScreen = () => {
  const isShowDetails = useContentStore((state: any) => state.isShowDetails);
  const setSearchKeyword = useContentStore(
    (state: any) => state.setSearchKeyword,
  );
  const setSearchKeywordTemp = useContentStore(
    (state: any) => state.setSearchKeywordTemp,
  );
  const setSelectedThumbnails = useContentStore(
    (state: any) => state.setSelectedThumbnails,
  );
  const setIsShowDetails = useContentStore(
    (state: any) => state.setIsShowDetails,
  );
  const selectedThumbnails = useContentStore(
    (state: any) => state.selectedThumbnails,
  );
  const isShowDeleteDialog = useContentStore(
    (state: any) => state.isShowDeleteDialog,
  );
  const setIsShowDeleteDialog = useContentStore(
    (state: any) => state.setIsShowDeleteDialog,
  );
  const setIsShowPlayer = useContentStore(
    (state: any) => state.setIsShowPlayer,
  );
  const setIsShowEdit = useContentStore((state: any) => state.setIsShowEdit);
  const isLoading = useContentStore((state: any) => state.isLoading);
  const setIsLoading = useContentStore((state: any) => state.setIsLoading);
  const setFilterTypes = useContentStore((state: any) => state.setFilterTypes);
  const isShowDeleteTagDialog = useContentStore(
    (state: any) => state.isShowDeleteTagDialog,
  );
  const setIsShowDeleteTagDialog = useContentStore(
    (state: any) => state.setIsShowDeleteTagDialog,
  );
  const [videoUrl, setVideoUrl] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [tempVideoTitle, setTempVideoTitle] = useState("");
  const tagDetails = useContentStore((state: any) => state.tagDetails);
  const setTagDetails = useContentStore((state: any) => state.setTagDetails);
  const [time, setTime] = useState("00:00:00");
  const [type, setType] = useState(TagTypeEnum.label);
  const [tag, setTag] = useState("");
  const [tagId, setTagId] = useState("");
  const [isCancelDisabled, setIsCancelDisabled] = useState(false);
  const isShowRenameDialog = useContentStore(
    (state: any) => state.isShowRenameDialog,
  );
  const setIsShowRenameDialog = useContentStore(
    (state: any) => state.setIsShowRenameDialog,
  );
  const isShowAddTagDialog = useContentStore(
    (state: any) => state.isShowAddTagDialog,
  );
  const setIsShowAddTagDialog = useContentStore(
    (state: any) => state.setIsShowAddTagDialog,
  );
  const isShowEditTagDialog = useContentStore(
    (state: any) => state.isShowEditTagDialog,
  );
  const setIsShowEditTagDialog = useContentStore(
    (state: any) => state.setIsShowEditTagDialog,
  );
  const [selectedRow, setSelectedRow] = useState<any>({});

  const navigate = useNavigate();

  const onClose = () => {
    navigate("/main");
    setIsShowDetails(false);
    setIsShowEdit(false);
    setIsShowPlayer(false);
    setSearchKeyword("");
    setSearchKeywordTemp("");
    setSelectedThumbnails([]);
    setVideoUrl("");
    setFilterTypes("");
  };

  const { videoId, searchKeyword }: any = useParams();

  const fetchData = async (videoId: any) => {
    const getUrl = await getVideoByID(videoId);
    if (getUrl.status === 200) {
      const data = getUrl.data;
      setVideoUrl(data);
    } else {
      handleError({
        status: getUrl.response.status,
        toast,
        errorMessage: "Error: Video might be deleted or not available.",
        onClose: () => {},
      });
    }

    const getDetails = await getVideoFromDB(videoId);
    if (getDetails.status === 200) {
      const data = getDetails.data;
      setVideoTitle(data.name);
      setTempVideoTitle(data.name);
      setTagDetails(data.tags);
    } else {
      handleError({
        status: getDetails.response.status,
        toast,
        errorMessage: "Error: Video might be deleted or not available.",
        onClose: () => {},
      });
    }
    setIsLoading(false);
  };

  const onCopyLinks = async () => {
    toast.warning(`Processing: Copying link`, {
      position: "bottom-left",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: true,
      pauseOnFocusLoss: false,
      progress: undefined,
      theme: "colored",
      transition: Bounce,
    });

    const copied = await getVideoByID(videoId);

    if (copied.status === 200) {
      navigator.clipboard.writeText(copied.data);
      toast.success("Video Link Copied", {
        position: "bottom-left",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
      });
    } else {
      handleError({
        status: copied.response.status,
        toast,
        errorMessage: "Error: Encountered an error while copying link.",
        onClose: () => {},
      });
    }
  };

  const renderDeleteDialog = () => {
    return (
      <Box sx={{ width: "100%" }}>
        <Stack spacing={1}>
          {selectedThumbnails.map((thumbnail: any) => (
            <Card variant="outlined">
              <CardContent style={{ padding: "1.25rem" }}>
                <Stack direction="row" gap="0.625rem">
                  <img
                    src={thumbnail.image}
                    style={{
                      height: "auto",
                      width: "2.5rem",
                      borderRadius: "0.25rem",
                      alignSelf: "stretch",
                    }}
                    alt={thumbnail.name}
                  />
                  {thumbnail.name}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  };

  const deleteVideos = async () => {
    setIsShowDeleteDialog(false);
    setSelectedThumbnails([]);
    toast.warning(`Processing: Deleting ${selectedThumbnails.length} video/s`, {
      position: "bottom-left",
      autoClose: 1000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: true,
      pauseOnFocusLoss: false,
      progress: undefined,
      theme: "colored",
      transition: Bounce,
    });

    const deleted = await deleteContentByID(videoId);

    if (deleted.status === 200) {
      toast.success("Video Deleted", {
        position: "bottom-left",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
        onClose: () => {
          window.location.assign("/main");
        },
      });
      setIsLoading(true);
    } else {
      handleError({
        status: deleted.response.status,
        toast,
        errorMessage: `Error: Encountered an error while deleting the video (CODE: ${deleted.code})`,
        onClose: () => {},
      });
    }
  };

  const onDownload = () => {
    let toastProcessing = toast.warning(
      "Processing: Generating Download links...",
      {
        position: "bottom-left",
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        autoClose: false,
        theme: "colored",
        transition: Bounce,
      },
    );

    Promise.allSettled(
      selectedThumbnails.map(async (selected: any) => {
        const getUrl = await getVideoByID(selected.videoIndexId);
        if (getUrl.status === 200) {
          const data = getUrl.data;

          await fetch(data, {
            headers: new Headers({
              Origin: window.location.origin,
            }),
            mode: "cors",
          })
            .then((response) => response.blob())
            .then((blob) => {
              let blobUrl = window.URL.createObjectURL(blob);
              var link = document.createElement("a");
              link.href = blobUrl;
              link.download = selected.name;
              link.click();

              toast.success(
                `Success: Download link generated for: ${selected.name}`,
                {
                  position: "bottom-left",
                  autoClose: 2000,
                  hideProgressBar: false,
                  closeOnClick: false,
                  pauseOnHover: false,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  theme: "colored",
                  transition: Bounce,
                },
              );
            })
            .catch((e) => {
              toast.error(
                `Error: Encountered issue downloading: ${selected.name}`,
                {
                  position: "bottom-left",
                  autoClose: 2000,
                  hideProgressBar: false,
                  closeOnClick: false,
                  pauseOnHover: false,
                  pauseOnFocusLoss: false,
                  draggable: true,
                  progress: undefined,
                  theme: "colored",
                  transition: Bounce,
                },
              );
            });
        } else {
          handleError({
            status: getUrl.response.status,
            toast,
            errorMessage: "Error: Video might be deleted or not available",
            onClose: () => {},
          });
        }
      }),
    ).then(() => toast.dismiss(toastProcessing));
  };

  const onClickEdit = async () => {
    navigate("/edit/" + videoId);
    setIsShowEdit(true);
    setIsShowDetails(false);
    const getUrl = await getVideoByID(videoId);
    if (getUrl.status === 200) {
      const data = getUrl.data;
      setVideoUrl(data);
    } else {
      setVideoUrl("");
      handleError({
        status: getUrl.response.status,
        toast,
        errorMessage: `Error: Encountered an error generating video URL. (CODE: ${getUrl.code})`,
        onClose: () => {},
      });
    }
  };

  const handleColor = (tag: any) => {
    if (!tag?.tagId) return darkLimeGreen;

    if (tag?.tagId && tag.deleted) return vividRed;

    if (tag?.tagId && tag.modified) return strongOrange;
  };

  const renderNumber = (index: number) => {
    return index + 1;
  };

  const TableHeader = (text: string) => {
    return (
      <TableCell
        style={{
          color: black,
          textOverflow: "ellipsis",
          fontSize: "1rem",
          fontWeight: 600,
          lineHeight: "normal",
          backgroundColor: antiFlashWhite,
        }}
        align="center"
      >
        {text}
      </TableCell>
    );
  };

  const renderDataTable = () => {
    return (
      <DataTable
        minWidth={535}
        label="details table"
        tableHeader={
          <>
            <TableCell style={{ backgroundColor: antiFlashWhite }}>
              {locale.hashtag}
            </TableCell>
            {TableHeader(locale.timestamp)}
            {TableHeader(locale.type)}
            {TableHeader(locale.tag)}
            {TableHeader(locale.confidence)}
          </>
        }
        tableBody={
          <TableBody>
            {tagDetails
              ?.filter(
                (item: any) => !item?.tagId || item.deleted || item.modified,
              )
              ?.map((item: any, index: any) => (
                <TableRow
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    cursor: "pointer",
                  }}
                  key={index}
                  hover={true}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    style={{ color: handleColor(item) }}
                  >
                    {renderNumber(index)}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ color: handleColor(item) }}
                  >
                    {convertTimeString(item.start)}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ color: handleColor(item) }}
                  >
                    {item.type}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ color: handleColor(item) }}
                  >
                    {item.name}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{ color: handleColor(item) }}
                  >
                    {toPercentage(item.confidence)}%
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        }
      />
    );
  };

  const validateTimeFormat = (time: string) => {
    const timeFormat = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
    return timeFormat.test(time);
  };

  const renderTagFormBody = () => {
    return (
      <Stack
        direction="column"
        spacing={2}
        gap="1.5rem"
        mt="24px"
        mb={type === TagTypeEnum.transcript ? "32px" : "0"}
      >
        <Stack direction="row" spacing={2}>
          <StyledTextFieldItem>
            <TextFieldInput
              label={locale.timestamp}
              value={time}
              onChange={(e: any) => setTime(e.target.value)}
              disabled={type === TagTypeEnum.general}
              error={type !== TagTypeEnum.general && !validateTimeFormat(time)}
              helperText={
                type !== TagTypeEnum.general &&
                !validateTimeFormat(time) &&
                "Invalid time format. Use HH:MM:SS."
              }
            />
          </StyledTextFieldItem>
          <StyledTextFieldItem>
            <FormControl fullWidth>
              <InputLabel id="select-type">{locale.type}</InputLabel>
              <StyledMUISelect
                variant="outlined"
                id="add-modal-select"
                labelId="select-label"
                label={locale.type}
                value={type}
                onChange={(e) => {
                  //@ts-ignore
                  setType(e.target.value);
                  if (e.target.value === TagTypeEnum.general) {
                    setTime("00:00:00");
                  }
                }}
              >
                {Object.values(TagTypeEnum)
                  .filter((type) => type !== TagTypeEnum.folder)
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      <Box
                        sx={{
                          alignItems: "center",
                          display: "flex",
                          gap: 1,
                        }}
                      >
                        <Circle
                          key={type}
                          toolTipTitle={type}
                          color={colorsCodes(type)}
                        />
                        {type}
                      </Box>
                    </MenuItem>
                  ))}
              </StyledMUISelect>
            </FormControl>
          </StyledTextFieldItem>
        </Stack>
        <Stack direction="row" spacing={2}>
          <StyledTextFieldItem>
            <TextFieldInput
              label={
                type === TagTypeEnum.transcript ? locale.transcript : locale.tag
              }
              value={tag}
              onChange={(e: any) => setTag(e.target.value)}
              multiline={type === TagTypeEnum.transcript}
              rows={type === TagTypeEnum.transcript ? 3 : 1}
            />
          </StyledTextFieldItem>
        </Stack>
      </Stack>
    );
  };

  const editVideoService = async (
    editVideoRequest: EditVideo,
    successMessage: string,
  ) => {
    const requestEditVideo = await editVideo(editVideoRequest);

    if (requestEditVideo.status === 200) {
      //setShowConfirmation(false);
      toast.success(successMessage, {
        position: "bottom-left",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        theme: "colored",
        transition: Bounce,
        onClose: () => {
          setIsCancelDisabled(false);
        },
      });
    } else {
      handleError({
        status: requestEditVideo.response.status,
        toast,
        errorMessage: `Error: Encountered an error. (CODE: ${requestEditVideo.code})`,
        onClose: () => {},
      });
    }
  };

  const onHandleCancel = () => {
    setTagDetails(tagDetails.slice(0));
  };

  const onSubmitDelete = async () => {
    const isTranscriptDeleted = tagDetails.find(
      (item: any) => item.deleted && item.type === "Transcript",
    );
    const editVideoRequest: EditVideo = {
      videoIndexId: videoId,
      name: videoTitle,
      description: videoTitle,
      newTags: [],
      editTags: [],
      deleteTags: tagDetails
        .filter((item: any) => item.deleted)
        .map((item: any) => {
          const deleteTag: DeleteTags = {
            tagId: item.tagId,
            type: item.type,
            tag: item.name,
          };

          return deleteTag;
        }),
    };
    setIsShowDeleteTagDialog(false);
    toast.warn(locale.cannot_cancel_saving, {
      position: "bottom-left",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      pauseOnFocusLoss: false,
      draggable: true,
      progress: undefined,
      theme: "colored",
      transition: Bounce,
    });
    await editVideoService(
      editVideoRequest,
      isTranscriptDeleted
        ? locale.transcript_delete_success
        : locale.tag_delete_success,
    );
    onHandleCancel();
    fetchData(videoId).finally(() => window.scrollTo(0, 0));
  };

  const onSubmitRename = async () => {
    const editVideoRequest: EditVideo = {
      videoIndexId: videoId,
      name: tempVideoTitle,
      description: tempVideoTitle,
      newTags: [],
      editTags: [],
      deleteTags: [],
    };

    setIsShowRenameDialog(false);
    toast.warn(locale.cannot_cancel_saving, {
      position: "bottom-left",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      pauseOnFocusLoss: false,
      draggable: true,
      progress: undefined,
      theme: "colored",
      transition: Bounce,
    });
    await editVideoService(editVideoRequest, locale.rename_video_success);
    onHandleCancel();
    fetchData(videoId).finally(() => window.scrollTo(0, 0));
  };

  const onSubmitTag = async (isEdit: boolean = false) => {
    const editVideoRequest: EditVideo = {
      videoIndexId: videoId,
      name: videoTitle,
      description: videoTitle,
      newTags:
        !isEdit && type && tag
          ? [
              {
                tag: tag.trim(),
                type: type,
                start: time,
                end: time,
              },
            ]
          : [],
      editTags:
        isEdit && type && tag
          ? [
              {
                tagId: tagId,
                tag: tag.trim(),
                type: type,
                start: time,
                end: time,
                oldTag: selectedRow?.name,
                oldType: selectedRow?.type,
              },
            ]
          : [],
      deleteTags: [],
    };

    const successMessage = isEdit
      ? type === TagTypeEnum.transcript
        ? locale.transcript_updated_success
        : locale.tag_updated_success
      : type === TagTypeEnum.transcript
        ? locale.transcript_added_success
        : locale.tag_added_success;

    setIsShowEditTagDialog(false);
    setIsShowAddTagDialog(false);
    toast.warn(locale.cannot_cancel_saving, {
      position: "bottom-left",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      pauseOnFocusLoss: false,
      draggable: true,
      progress: undefined,
      theme: "colored",
      transition: Bounce,
    });
    await editVideoService(editVideoRequest, successMessage);
    onHandleCancel();
    fetchData(videoId).finally(() => {
      setTag("");
      setTime("00:00:00");
      setType(TagTypeEnum.label);
      window.scrollTo(0, 0);
    });
  };

  useEffect(() => {
    setIsLoading(true);
    setIsShowDetails(true);
    if (videoId) fetchData(videoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  useEffect(() => {
    if (isShowAddTagDialog) {
      setTag("");
      setTime("00:00:00");
      setType(TagTypeEnum.label);
    }
  }, [isShowAddTagDialog]);

  return (
    <>
      <TopAppBar
        isShowDetails={isShowDetails}
        pageTitle={locale.details}
        btnClose={locale.close}
        onClose={() => onClose()}
        isCancelDisabled={isCancelDisabled ? "disabled" : ""}
      />
      <MainLayout>
        {!isLoading ? (
          <Layout
            url={videoUrl}
            label={locale.video_title}
            videoId={videoId}
            tagLabel={locale.tag}
            title={videoTitle}
            tagDetails={tagDetails}
            searchKeyword={searchKeyword}
            onCopyLinks={() => onCopyLinks()}
            onDownload={() => onDownload()}
            onClickEdit={() => onClickEdit()}
            isShowEdit={true}
            setTag={setTag}
            setType={setType}
            setTime={setTime}
            setTagId={setTagId}
            setSelectedRow={setSelectedRow}
            onClickRename={() => setIsShowRenameDialog(true)}
            onClickEditTag={() => {
              setTagId(selectedRow.tagId);
              setTag(selectedRow.name.trim());
              setType(selectedRow.type);
              setTime(selectedRow.start.toString().substring(0, 8));
              setIsShowEditTagDialog(true);
            }}
          />
        ) : (
          <></>
        )}
      </MainLayout>
      {isShowDeleteDialog ? (
        <DialogBox
          header={locale.deleteHeader}
          body={<div>{locale.deleteBody}</div>}
          subBody={renderDeleteDialog()}
          onCancel={() => setIsShowDeleteDialog(false)}
          onSubmit={() => deleteVideos()}
          isDelete={true}
          primaryButton={locale.yes}
          secondaryButton={locale.no}
        ></DialogBox>
      ) : (
        <></>
      )}
      {isShowDeleteTagDialog ? (
        <DialogBox
          header={
            tagDetails.find(
              (item: any) => item.deleted && item.type === "Transcript",
            )
              ? locale.deleteTranscript
              : locale.deleteTag
          }
          body={
            <div>
              {tagDetails.find(
                (item: any) => item.deleted && item.type === "Transcript",
              )
                ? locale.delete_transcript_body
                : locale.delete_tags_body}
            </div>
          }
          subBody={renderDataTable()}
          onCancel={() => {
            setTagDetails(
              tagDetails.map((item: any) => ({ ...item, deleted: false })),
            );
            setIsShowDeleteTagDialog(false);
          }}
          onSubmit={() => onSubmitDelete()}
          isDelete={false}
          primaryButton={locale.yes}
          secondaryButton={locale.no}
        ></DialogBox>
      ) : (
        <></>
      )}
      {isShowEditTagDialog ? (
        <DialogBox
          header={locale.edit_tag_header}
          body={renderTagFormBody()}
          subBody={<></>}
          onCancel={() => {
            setTag("");
            setTime("00:00:00");
            setType(TagTypeEnum.label);
            setIsShowEditTagDialog(false);
          }}
          onSubmit={() => onSubmitTag(true)}
          isDelete={false}
          primaryButton={locale.update}
          secondaryButton={locale.cancel}
          disablePrimary={
            tag.trim() === "" ||
            (tag === selectedRow?.name && type === selectedRow?.type && type !== TagTypeEnum.general && time === selectedRow?.start.toString().substring(0, 8))
            || (tag === selectedRow?.name && type === selectedRow?.type && type === TagTypeEnum.general )
          }
        ></DialogBox>
      ) : (
        <></>
      )}
      {isShowAddTagDialog ? (
        <DialogBox
          header={locale.add_tag_header}
          body={renderTagFormBody()}
          subBody={<></>}
          onCancel={() => {
            setTag("");
            setTime("00:00:00");
            setType(TagTypeEnum.label);
            setIsShowAddTagDialog(false);
          }}
          onSubmit={() => onSubmitTag()}
          isDelete={false}
          primaryButton={locale.add}
          secondaryButton={locale.cancel}
          disablePrimary={tag.trim() === ""}
        ></DialogBox>
      ) : (
        <></>
      )}
      {isShowRenameDialog ? (
        <DialogBox
          header={locale.control_rename}
          body={<></>}
          subBody={
            <div>
              <TextFieldInput
                value={tempVideoTitle}
                onChange={(e: any) => setTempVideoTitle(e.target.value)}
              />
            </div>
          }
          onCancel={() => {
            setTempVideoTitle(videoTitle);
            setIsShowRenameDialog(false);
          }}
          onSubmit={() => onSubmitRename()}
          isDelete={false}
          primaryButton={locale.save}
          secondaryButton={locale.cancel}
          disablePrimary={
            tempVideoTitle.trim() === "" || tempVideoTitle === videoTitle
          }
        ></DialogBox>
      ) : (
        <></>
      )}
    </>
  );
};

export default DetailsScreen;
